import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API = axios.create({ baseURL: '/api/v1/mmsp' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

function apiErrorMessage(err: unknown, fallback: string) {
  const data = (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data;
  return data?.message || data?.error || (err as { message?: string })?.message || fallback;
}

export const MmspClubDetailOverlay: React.FC = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      const res = await API.get(`/ops/clubs/${clubId}/onboarding`);
      setData(res.data?.data);
    } catch (err: any) {
      setError(apiErrorMessage(err, 'Could not load club.'));
    }
  }, [clubId]);

  useEffect(() => { load(); }, [load]);

  const club = data?.club;
  const constitution = data?.constitution;

  const run = async (path: string, label: string) => {
    setBusy(label);
    setError('');
    try {
      await API.post(path);
      await load();
    } catch (err: any) {
      setError(apiErrorMessage(err, `Could not ${label}.`));
    } finally {
      setBusy('');
    }
  };

  if (!club) {
    return <p className="text-sm text-[var(--muted-foreground)]">{error || 'Loading…'}</p>;
  }

  return (
    <div className="space-y-6">
      <button className="text-sm text-[var(--mymoolah-blue)]" onClick={() => navigate('/admin/mmsp')}>Back to clubs</button>
      <div className="rounded-xl border bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold">{club.name}</h2>
            <p className="text-sm text-[var(--muted-foreground)]">{club.status} · {club.onboardingStatus || '—'}</p>
            <p className="mt-2 text-sm">Club code <span className="font-mono">{club.clubCode}</span></p>
          </div>
          <div className="flex gap-2">
            {club.onboardingStatus === 'setup_submitted' && (
              <button disabled={!!busy} className="rounded-lg bg-[var(--mymoolah-blue)] px-4 py-2 text-sm text-white" onClick={() => run(`/ops/clubs/${club.id}/activation/maker`, 'maker review')}>
                Maker review
              </button>
            )}
            {club.onboardingStatus === 'maker_reviewed' && (
              <button disabled={!!busy} className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm text-white" onClick={() => run(`/ops/clubs/${club.id}/activation/checker`, 'activate')}>
                Checker activate
              </button>
            )}
          </div>
        </div>
        {club.commonBond && <p className="mt-4 text-sm">{club.commonBond}</p>}
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>

      <div className="rounded-xl border bg-white p-5">
        <h3 className="font-semibold">Constitution</h3>
        {constitution ? (
          <div className="mt-2 space-y-2 text-sm">
            <p>Version {constitution.version} · hash {constitution.rulesHash}</p>
            <p>{constitution.summary}</p>
            <p className="text-[var(--muted-foreground)]">Adopted: {constitution.adoptedAt ? new Date(constitution.adoptedAt).toLocaleString() : 'Not yet'}</p>
          </div>
        ) : <p className="mt-2 text-sm">No constitution yet.</p>}
      </div>

      <div className="rounded-xl border bg-white p-5">
        <h3 className="font-semibold">Chapters on for this club</h3>
        <ul className="mt-2 text-sm">
          {(data.chapters || []).filter((ch: any) => ch.code !== 'main').map((ch: any) => (
            <li key={ch.id}>{ch.name} · {ch.type} · R{Number(ch.contributionAmountZar || 0).toFixed(2)} {ch.contributionFrequency}</li>
          ))}
          {(!data.chapters || data.chapters.filter((ch: any) => ch.code !== 'main').length === 0) && (
            <li>Savings is the day-1 default until the chair turns on more chapters.</li>
          )}
        </ul>
      </div>
    </div>
  );
};
